import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { HashRouter } from "react-router-dom";
import { extractTenant, isLocalHostname } from "@/lib/hostname";
import { resolveApiBaseUrl } from "@/lib/runtimeOrigin";
import { isDemoMode } from "@/services/mode";
import App from "@/App";

// 🛑 GLOBAL DEMO GUARD: Install immediately to intercept ALL network traffic
// before components or services (like heartbeat/telemetry) can fire.
(function bootDemo() {
  try {
    // Direct hostname check to avoid depending on complex imports this early
    const host = typeof window !== "undefined" ? String(window.location.hostname || "").toLowerCase() : "";
    const isDemo = host.includes("demo") || process.env.REACT_APP_MODE === "demo";
    if (isDemo) {
      // Dynamic import to avoid bundling router/seed in production unless hit
      import("./services/demo/networkGuard").then(m => m.installDemoNetworkGuards());
    }
  } catch (e) {
    console.error("Demo boot failed", e);
  }
})();

function applyBusinessContextFromHostname() {
  if (typeof window === "undefined") return;
  try {
    const host = String(window.location.hostname || "").toLowerCase().trim();
    const slug = extractTenant(host);
    if (!slug) return;
    window.localStorage.setItem("jewelflow_active_business_id", slug);
  } catch {
    // ignore storage errors
  }
}

function registerPwaShellWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // DEMO: never register a service worker. The demo is purely client-side and a
  // SW could intercept/relay requests or cache stale builds. Proactively
  // unregister any existing JewelFlow SW so the demo origin stays clean.
  if (isDemoMode()) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
    return;
  }
  const isElectron = Boolean(window.JEWEL_FLOW_ENV && window.JEWEL_FLOW_ENV.isElectron) || window.location.protocol === "file:";
  if (isElectron) return;
  const isLocalDev =
    process.env.NODE_ENV !== "production" ||
    isLocalHostname(window.location.hostname) ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "::1";
  if (isLocalDev) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) =>
          Promise.all(
            registrations.map(async (registration) => {
              const scriptUrl =
                registration.active?.scriptURL ||
                registration.waiting?.scriptURL ||
                registration.installing?.scriptURL ||
                "";
              if (String(scriptUrl).includes("/jewelflow-sw.js")) {
                await registration.unregister();
              }
            })
          )
        )
        .then(() => caches?.delete?.("jewelflow-shell-v1"))
        .catch(() => {});
    });
    return;
  }
  window.addEventListener("load", () => {
    const base = process.env.PUBLIC_URL || "";
    navigator.serviceWorker.register(`${base}/jewelflow-sw.js`).catch(() => {});
  });
}

function resolveBootstrapApiBase() {
  if (typeof window === "undefined") return "/api";
  const resolved = resolveApiBaseUrl();
  if (resolved) return resolved.replace(/\/+$/, "");
  const origin = `${window.location.protocol}//${window.location.host}`;
  return isLocalHostname(window.location.hostname) ? `${origin.replace(/\/+$/, "")}/api` : `${origin.replace(/\/+$/, "")}/api`;
}

applyBusinessContextFromHostname();
registerPwaShellWorker();

const pushEarlyClientErrorLog = (entry) => {
  if (typeof window === "undefined") return;

  const payload = {
    ts: new Date().toISOString(),
    scope: "bootstrap",
    level: "error",
    route: `${window.location.pathname}${window.location.hash || ""}`,
    ...entry,
  };

  try {
    const key = "mmj_client_error_log";
    const existing = JSON.parse(window.localStorage.getItem(key) || "[]");
    const next = [...existing.slice(-99), payload];
    window.localStorage.setItem(key, JSON.stringify(next));
    window.__MMJClientErrorLog = next;
  } catch {
    // ignore local storage failures
  }

  // DEMO: keep the error log local only — never POST it anywhere.
  if (isDemoMode()) return;

  try {
    const apiBase = resolveBootstrapApiBase();
    fetch(`${apiBase}/client-log`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore network logging failures
  }
};

window.addEventListener("error", (event) => {
  pushEarlyClientErrorLog({
    stage: "bootstrap-window-error",
    message: event?.message || event?.error?.message || "Unhandled bootstrap window error",
    stack: event?.error?.stack || "",
    source: event?.filename || "",
    line: event?.lineno || 0,
    column: event?.colno || 0,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event?.reason;
  pushEarlyClientErrorLog({
    stage: "bootstrap-unhandled-rejection",
    message:
      reason?.message ||
      (typeof reason === "string" ? reason : "Unhandled bootstrap promise rejection"),
    stack: reason?.stack || "",
  });
});

async function initElectronEnv() {
  if (window.electronAPI && window.electronAPI.getEnvInfo) {
    try {
      const info = await window.electronAPI.getEnvInfo();
      if (info && !info.error) {
        window.JEWEL_FLOW_ENV = { ...(window.JEWEL_FLOW_ENV || {}), ...info };
      }
    } catch (err) {
      console.error("[JewelFlow] Failed to load Electron env info:", err);
    }
  }
}

async function renderApp() {
  await initElectronEnv();

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <HashRouter>
      <App />
    </HashRouter>
  );
}

renderApp();

// 🔥 iPad viewport fix (MUST)
function fixViewportScale() {
  const meta = document.querySelector("meta[name=viewport]");
  if (!meta) return;

  meta.setAttribute(
    "content",
    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
  );
}

// Fix on rotate
window.addEventListener("orientationchange", () => {
  setTimeout(fixViewportScale, 300);
});

// Fix on resize (Safari bug)
window.addEventListener("resize", fixViewportScale);
